import { assert, Callback1, Destructor, NullFunction, VoidCallback } from '@deqo/foundation';
import { useEffect, useState } from 'react';
import { ReadStore } from '../../concepts/revive/ReadStore';
import { StoreObserver } from '../../concepts/revive/Store';

export interface StatePropertyRef<T> {
    readonly name: string;
    readonly empty?: T;
}

export function StatePropertyRef<T>(name: string, empty?: T): StatePropertyRef<T> {
    if (empty) Object.freeze(empty);
    return Object.freeze({
        name,
        empty,
    });
}

// eslint-disable-next-line max-len
export function StatePropertyRef2<T, A extends any[], C extends ((...args: A) => T)>(name: string, create: C, empty?: T): { create: C } & StatePropertyRef<T> {
    return Object.freeze({ name, create, empty });
}

export interface StateProperty<T> {
    readonly name: string;
    readonly writable: boolean;
    get(): T;
    watch(callback: VoidCallback): Destructor;
}

export type StatePropertyFactory<T> = (store: ExtendableStore) => StateProperty<T>;
export type StatePropertyFactoryGenerator<T> = (store: ExtendableStore) => Pick<StateProperty<T>, 'get' | 'watch'>;

export const StateProperty = {
    alias<P, K extends keyof P, T extends P[K]>(ref: StatePropertyRef<T>, prop: StatePropertyRef<P>, key: K) {
        return StateProperty.derive(ref, store => ({
            get: () => store.get(prop)[key],
            watch: f => store.observe(prop, (n, p) => {
                if (n[key] !== p[key]) f();
            }),
        }));
    },
    const<T>({ name }: StatePropertyRef<T>, value: T) {
        const writable = false;
        const get = () => value;
        const watch = (_: VoidCallback) => NullFunction;
        return { name, writable, get, watch };
    },
    derive<T>({ name }: StatePropertyRef<T>, generator: StatePropertyFactoryGenerator<T>) {
        return store => {
            const writable = false;
            const { get, watch } = generator(store);
            return { name, writable, get, watch };
        };
    },
    variable<T>({ name }: StatePropertyRef<T>, value: T) {
        const writable = true;
        const get = () => value;
        const watch = (_: VoidCallback) => NullFunction;
        return { name, writable, get, watch };
    },
};

// export const inMetaProperties = (...properties: StateProperty<any>[]): Connector =>
//     r => void (properties.forEach(x => r.call(ExtendableStore).extend(x)));

export class ExtendableStore extends ReadStore<Record<string, any>> {

    private readonly properties = new Map<string, StateProperty<any>>();

    protected state: Record<string, any> = {};

    public extend<T>(input: StateProperty<T> | StatePropertyFactory<T>): this {
        const prop = (typeof (input) === 'function') ? input(this) : input;
        assert(!this.properties.has(prop.name), `ExtendableStore: ${prop.name} is already registered.`);
        this.properties.set(prop.name, prop);
        this.burden(prop.watch(() => this.mutate(x => x[prop.name] = prop.get())));
        this.repair(x => x[prop.name] = prop.get());
        return this;
    }

    public get<T>(ref: StatePropertyRef<T>): T {
        return this.state[ref.name];
    }

    public apply<T>(ref: StatePropertyRef<T>, mutator: Callback1<T, T>): T {
        assert(!!this.properties.get(ref.name), `ExtendableStore: ${ref.name} does not exist.`);
        assert(!!this.properties.get(ref.name).writable, `ExtendableStore: ${ref.name} is readonly.`);
        const os = this.mutate(x => x[ref.name] = mutator(x[ref.name]));
        return os[ref.name];
    }

    public patch<T>(ref: StatePropertyRef<T>, patch: Partial<T>): T {
        assert(!!this.properties.get(ref.name), `ExtendableStore: ${ref.name} does not exist.`);
        assert(!!this.properties.get(ref.name).writable, `ExtendableStore: ${ref.name} is readonly.`);
        const os = this.mutate(x => Object.assign(x[ref.name], patch));
        return os[ref.name];
    }

    public update<T>(ref: StatePropertyRef<T>, value: T): T {
        assert(!!this.properties.get(ref.name), `ExtendableStore: ${ref.name} does not exist.`);
        assert(!!this.properties.get(ref.name).writable, `ExtendableStore: ${ref.name} is readonly.`);
        const os = this.mutate(x => x[ref.name] = value);
        return os[ref.name];
    }

    public touch<T>(ref: StatePropertyRef<T>): T {
        return this.update(ref, this.get(ref));
    }

    public observe(observer: StoreObserver<Record<string, any>>): Destructor;
    public observe<P>(path: Callback1<P, Record<string, any>>, observer: StoreObserver<P>): Destructor;
    public observe<P>(ref: StatePropertyRef<P>, observer: StoreObserver<P>): Destructor;

    // TODO: Probably optimize this using a custom impl of Store
    public observe(a: any, b?: any): Destructor {
        if (typeof (a) === 'object' && 'name' in a) {
            return super.observe(x => x[a.name], b);
        }
        else {
            return super.observe(a, b);
        }
    }
}

export type ObserverProps<T> = { store: ExtendableStore, render: Callback1<React.ReactElement<any>, T> };

export const createObserver = <T extends unknown>(property: StatePropertyRef<T>): React.FC<ObserverProps<T>> =>
    ({ store, render }) => {
        const [state, setState] = useState(store.get(property));
        useEffect(() => store.observe(property, setState), [store, setState]);
        return render(state);
    };
